(() => {
  if (customElements.get('aiovo-fit-finder')) return;

  // Finder row layout: inch | mm | US | UK | France | Germany | Japan | Italy.
  const COL = { inch: 0, mm: 1, us: 2, uk: 3, fr: 4, de: 5, jp: 6, it: 7 };
  const NONE = '—';

  // Each chart row rendered by snippets/aiovo-fit-size-data.liquid carries its full data,
  // so the finder works whichever sizing-system columns the merchant shows.
  function readData(rows) {
    return rows.map((tr) => tr.dataset.aiovoFitRow.split('|').map((value) => (value === '---' ? NONE : value)));
  }

  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  class AiovoFitFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.setup();
    }

    setup() {
      const root = this;
      const $ = (selector) => root.querySelector(selector);
      const $$ = (selector) => Array.from(root.querySelectorAll(selector));
      const rows = $$('[data-aiovo-fit-row]');
      const DATA = readData(rows);
      if (!DATA.length) return;
      const text = root.dataset;
      const LO = parseFloat(DATA[0][COL.mm]);
      const HI = parseFloat(DATA[DATA.length - 1][COL.mm]);
      const range = DATA[0][COL.mm] + '–' + DATA[DATA.length - 1][COL.mm] + ' ' + text.unitMm;

      const chips = $$('[data-aiovo-fit-sys]');
      const state = { sys: chips.length ? chips[0].dataset.aiovoFitSys : 'mm', idx: Math.max(0, DATA.findIndex((r) => r[COL.us] === text.defaultUs)) };
      const pills = $('[data-aiovo-fit-pills]');
      const sizeGroup = $('[data-aiovo-fit-size-group]');
      const sizeLabel = $('[data-aiovo-fit-size-label]');
      const mmGroup = $('[data-aiovo-fit-mm-group]');
      const mmInput = $('[data-aiovo-fit-mm]');
      const mmNote = $('[data-aiovo-fit-mm-note]');
      const tableWrap = $('[data-aiovo-fit-table]');
      const tabs = $$('[data-aiovo-fit-tab]');
      const outputs = $$('[data-aiovo-fit-out]');
      const byId = (id) => root.querySelector('#' + CSS.escape(id));

      function nearestWith(col, idx) {
        if (DATA[idx][col] !== NONE) return idx;
        for (let k = 1; k < DATA.length; k++) {
          if (idx + k < DATA.length && DATA[idx + k][col] !== NONE) return idx + k;
          if (idx - k >= 0 && DATA[idx - k][col] !== NONE) return idx - k;
        }
        return idx;
      }

      function setNote(kind) {
        const r = DATA[state.idx];
        const message = { empty: text.msgEmpty, exact: text.msgExact, near: text.msgNear, low: text.msgLow, high: text.msgHigh }[kind];
        mmNote.textContent = fill(message, { us: r[COL.us], mm: r[COL.mm], range });
        mmNote.classList.toggle('is-error', kind === 'low' || kind === 'high');
        mmNote.classList.toggle('is-muted', kind === 'empty');
      }

      function renderSystem() {
        chips.forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.aiovoFitSys === state.sys)));
        const isMm = state.sys === 'mm';
        sizeGroup.hidden = isMm;
        mmGroup.hidden = !isMm;
        if (!isMm) {
          const chip = chips.find((c) => c.dataset.aiovoFitSys === state.sys);
          sizeLabel.textContent = fill(text.sizeLabel, { system: chip ? chip.dataset.aiovoFitAdjective : '' });
        }
      }

      function renderPills() {
        if (state.sys === 'mm') return;
        const col = COL[state.sys];
        const fragment = document.createDocumentFragment();
        DATA.forEach((r, i) => {
          if (r[col] === NONE) return;
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'aiovo-fit-pill';
          pill.dataset.aiovoFitIdx = String(i);
          pill.setAttribute('aria-pressed', String(i === state.idx));
          pill.textContent = r[col];
          fragment.appendChild(pill);
        });
        pills.replaceChildren(fragment);
      }

      function renderResult() {
        const r = DATA[state.idx];
        outputs.forEach((el) => {
          const key = el.dataset.aiovoFitOut;
          if (key === 'mm') el.textContent = r[COL.mm] + ' ' + text.unitMm;
          else if (key === 'inch') el.textContent = r[COL.inch] + ' ' + text.unitIn;
          else if (key === 'label') el.textContent = fill(text.highlight, { us: r[COL.us] });
          else el.textContent = r[COL[key]];
        });
        rows.forEach((tr, i) => {
          const on = i === state.idx;
          tr.classList.toggle('is-selected', on);
          if (on) tr.setAttribute('aria-current', 'true');
          else tr.removeAttribute('aria-current');
        });
      }

      function pickSystem(id) {
        state.sys = id;
        if (id === 'mm') {
          mmInput.value = DATA[state.idx][COL.mm];
          setNote('exact');
        } else {
          state.idx = nearestWith(COL[id], state.idx);
        }
        renderSystem();
        renderPills();
        renderResult();
      }

      function onMm() {
        const raw = String(mmInput.value || '');
        const value = parseFloat(raw);
        if (!raw.trim() || Number.isNaN(value)) return setNote('empty');
        if (value < LO - 0.3) return setNote('low');
        if (value > HI + 0.3) return setNote('high');
        let best = 0;
        let bestDiff = Infinity;
        // Nearest mm row, algorithm unchanged from the approved reference. The rule for values exactly
        // halfway between two rows is NOT settled: handoff §5 says "larger size", checklist §7 expects
        // 57.3 → US 8. Today the result depends on floating-point subtraction (57.3 → US 8), which is
        // not a formal rule. Pending the owner's decision; see docs/find-your-fit/IMPLEMENTATION.md.
        DATA.forEach((r, i) => {
          const diff = Math.abs(parseFloat(r[COL.mm]) - value);
          if (diff <= bestDiff) {
            bestDiff = diff;
            best = i;
          }
        });
        state.idx = best;
        setNote(bestDiff < 0.05 ? 'exact' : 'near');
        renderResult();
      }

      function scrollChartToSelection() {
        const tr = rows[state.idx];
        if (!tableWrap || !tr) return;
        const head = tableWrap.querySelector('thead');
        tableWrap.scrollTop = Math.max(0, tr.offsetTop - (head ? head.offsetHeight : 0) - 96);
      }

      function setView(view, focus) {
        tabs.forEach((tab) => {
          const on = tab.dataset.aiovoFitTab === view;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
          const panel = byId(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
          if (on && focus) tab.focus();
        });
        if (view === 'chart') scrollChartToSelection();
      }

      chips.forEach((chip) => chip.addEventListener('click', () => pickSystem(chip.dataset.aiovoFitSys)));

      pills.addEventListener('click', (event) => {
        const pill = event.target.closest('[data-aiovo-fit-idx]');
        if (!pill) return;
        state.idx = parseInt(pill.dataset.aiovoFitIdx, 10);
        renderPills();
        renderResult();
        const again = pills.querySelector('[data-aiovo-fit-idx="' + state.idx + '"]');
        if (again) again.focus();
      });

      mmInput.addEventListener('input', onMm);

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => setView(tab.dataset.aiovoFitTab));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const next = event.key === 'Home' ? 'finder' : event.key === 'End' ? 'chart' : tab.dataset.aiovoFitTab === 'finder' ? 'chart' : 'finder';
          setView(next, true);
        });
      });

      // Used by any [data-fyf-mm-link] on the page (e.g. the paper method section).
      this.enterMeasurement = () => {
        setView('finder');
        pickSystem('mm');
        setTimeout(() => {
          mmInput.focus({ preventScroll: true });
          mmInput.select();
        }, 450);
      };

      if (state.sys !== 'mm') state.idx = nearestWith(COL[state.sys], state.idx);
      renderSystem();
      renderPills();
      renderResult();
    }
  }

  customElements.define('aiovo-fit-finder', AiovoFitFinder);

  // Sections stay decoupled: the "Enter my measurement" link only carries data-fyf-mm-link
  // and a link to the finder's anchor. The link's own navigation handles the scroll.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-fyf-mm-link]');
    if (!link || !link.hash || link.pathname !== location.pathname) return;
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    } catch (error) {
      target = null;
    }
    const finder = target && target.matches('aiovo-fit-finder') ? target : target?.closest('aiovo-fit-finder') || document.querySelector('aiovo-fit-finder');
    if (finder && typeof finder.enterMeasurement === 'function') finder.enterMeasurement();
  });
})();
